import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScanLine,
  Package,
  Wrench,
  Layers,
  AlertCircle,
  Clock,
  MapPin,
  ArrowRight,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader, DataTable, Column } from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { toast } from 'sonner';
import {
  barcodeApi,
  ScanResult,
  ScanResultType,
  InventoryItemScanResult,
  EquipmentScanResult,
  BatchScanResult,
} from '@/api/services/barcode.api';
import { formatDate } from '@/lib/utils';

interface ScanHistoryItem {
  code: string;
  result: ScanResult;
  timestamp: Date;
}

export default function QuickScan() {
  const [scanCode, setScanCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [currentResult, setCurrentResult] = useState<ScanResult | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleScan = useCallback(async () => {
    if (!scanCode.trim()) return;

    setIsScanning(true);
    try {
      const response = await barcodeApi.scan(scanCode.trim());
      const result = response.data;
      setCurrentResult(result);

      // Add to history
      setScanHistory((prev) => [
        { code: scanCode.trim(), result, timestamp: new Date() },
        ...prev.slice(0, 9), // Keep last 10
      ]);

      if (result.type === ScanResultType.NOT_FOUND) {
        toast.error(result.message || 'No item found for this barcode');
      } else {
        toast.success(`Found ${result.type.replace('_', ' ').toLowerCase()}`);
      }
    } catch {
      toast.error('Failed to scan barcode. Please try again.');
    } finally {
      setIsScanning(false);
      setScanCode('');
      inputRef.current?.focus();
    }
  }, [scanCode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const clearHistory = () => {
    setScanHistory([]);
    setCurrentResult(null);
  };

  const navigateToDetail = (result: ScanResult) => {
    switch (result.type) {
      case ScanResultType.INVENTORY_ITEM:
        navigate(`/inventory/${result.inventoryItem?.id}`);
        break;
      case ScanResultType.EQUIPMENT:
        navigate(`/equipment/${result.equipment?.id}`);
        break;
      case ScanResultType.BATCH:
        navigate(`/inventory/${result.batch?.itemId}`);
        break;
    }
  };

  const getResultIcon = (type: ScanResultType) => {
    switch (type) {
      case ScanResultType.INVENTORY_ITEM:
        return <Package className="h-5 w-5 text-status-info" />;
      case ScanResultType.EQUIPMENT:
        return <Wrench className="h-5 w-5 text-primary" />;
      case ScanResultType.BATCH:
        return <Layers className="h-5 w-5 text-status-success" />;
      default:
        return <AlertCircle className="h-5 w-5 text-status-destructive" />;
    }
  };

  const getResultTypeLabel = (type: ScanResultType) => {
    switch (type) {
      case ScanResultType.INVENTORY_ITEM:
        return 'Inventory Item';
      case ScanResultType.EQUIPMENT:
        return 'Equipment';
      case ScanResultType.BATCH:
        return 'Batch';
      default:
        return 'Not Found';
    }
  };

  const scanHistoryColumns: Column<ScanHistoryItem>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (item) => (
        <div className="flex items-center gap-2">
          {getResultIcon(item.result.type)}
          <span className="text-sm">{getResultTypeLabel(item.result.type)}</span>
        </div>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      render: (item) => <span className="font-mono text-sm">{item.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (item) => (
        <>
          {item.result.inventoryItem?.name ||
            item.result.equipment?.name ||
            item.result.batch?.itemName ||
            '-'}
        </>
      ),
    },
    {
      key: 'timestamp',
      header: 'Time',
      render: (item) => (
        <span className="text-muted-foreground text-sm">{item.timestamp.toLocaleTimeString()}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-[80px]',
      render: (item) =>
        item.result.type !== ScanResultType.NOT_FOUND ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigateToDetail(item.result);
            }}
          >
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : null,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Quick Scan"
          description="Barcode lookup across stock items, assets, batches, and storage bins."
        />
      </motion.div>

      {/* Scan Input */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <ScanLine className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={scanCode}
                  onChange={(e) => setScanCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Scan or enter barcode..."
                  className="h-14 pl-10 text-lg"
                  autoFocus
                />
              </div>
              <Button
                onClick={handleScan}
                disabled={isScanning || !scanCode.trim()}
                className="h-14 px-8"
              >
                {isScanning ? 'Scanning...' : 'Scan'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Current Result */}
        {currentResult && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <h3 className="text-base font-semibold text-foreground">
                {getResultIcon(currentResult.type)}
                {getResultTypeLabel(currentResult.type)}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Clear scan result"
                onClick={() => setCurrentResult(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {currentResult.type === ScanResultType.INVENTORY_ITEM &&
                currentResult.inventoryItem && (
                  <InventoryItemResult
                    item={currentResult.inventoryItem}
                    onNavigate={() => navigateToDetail(currentResult)}
                  />
                )}

              {currentResult.type === ScanResultType.EQUIPMENT && currentResult.equipment && (
                <EquipmentResult
                  equipment={currentResult.equipment}
                  onNavigate={() => navigateToDetail(currentResult)}
                />
              )}

              {currentResult.type === ScanResultType.BATCH && currentResult.batch && (
                <BatchResult
                  batch={currentResult.batch}
                  onNavigate={() => navigateToDetail(currentResult)}
                />
              )}

              {currentResult.type === ScanResultType.NOT_FOUND && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-5 w-5" />
                  <span>{currentResult.message}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scan History */}
        {scanHistory.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <h3 className="text-base font-semibold text-foreground">Recent Scans</h3>
              <Button variant="ghost" size="sm" onClick={clearHistory}>
                <Trash2 className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </CardHeader>
            <CardContent>
              <DataTable<ScanHistoryItem>
                pagination={false}
                data={scanHistory}
                columns={scanHistoryColumns}
              />
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.main>
  );
}

// Sub-components for different result types

function InventoryItemResult({
  item,
  onNavigate,
}: {
  item: InventoryItemScanResult;
  onNavigate: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold">{item.name}</h3>
          <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>
          {item.description && (
            <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
          )}
        </div>
        <Button onClick={onNavigate}>
          View Details
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Category</p>
          <p className="font-medium">{item.category}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Total Stock</p>
          <p className="font-medium">
            {item.totalStock} {item.unit}
          </p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Unit Cost</p>
          <p className="font-medium">{item.unitCost ? `$${item.unitCost.toFixed(2)}` : '-'}</p>
        </div>
      </div>

      {item.levels.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Stock by Location</h4>
          <DataTable
            pagination={false}
            data={item.levels}
            columns={[
              {
                key: 'locationName',
                header: 'Location',
                render: (level: InventoryItemScanResult['levels'][number]) => (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    {level.locationName}
                    <span className="text-xs text-muted-foreground">({level.locationCode})</span>
                  </div>
                ),
              },
              {
                key: 'quantity',
                header: 'Quantity',
                className: 'text-right',
                sortable: true,
              },
              {
                key: 'availableQty',
                header: 'Available',
                className: 'text-right',
                sortable: true,
              },
            ]}
            getRowId={(level) => level.locationId}
          />
        </div>
      )}
    </div>
  );
}

function EquipmentResult({
  equipment,
  onNavigate,
}: {
  equipment: EquipmentScanResult;
  onNavigate: () => void;
}) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-status-success-subtle text-status-success-foreground';
      case 'IN_USE':
        return 'bg-status-info-subtle text-status-info-foreground';
      case 'MAINTENANCE':
        return 'bg-status-warning-subtle text-status-warning-foreground';
      case 'REPAIR':
        return 'bg-status-destructive-subtle text-status-destructive-foreground';
      default:
        return 'bg-muted text-foreground';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold">{equipment.name}</h3>
          <p className="text-sm text-muted-foreground">Asset #: {equipment.assetNumber}</p>
          {equipment.description && (
            <p className="text-sm text-muted-foreground mt-1">{equipment.description}</p>
          )}
        </div>
        <Button onClick={onNavigate}>
          View Details
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Badge className={getStatusColor(equipment.status)}>{equipment.status}</Badge>
        <Badge variant="outline">{equipment.condition}</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Category</p>
          <p className="font-medium">{equipment.category}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Location</p>
          <p className="font-medium">{equipment.locationName}</p>
        </div>
        {equipment.manufacturer && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Manufacturer</p>
            <p className="font-medium">{equipment.manufacturer}</p>
          </div>
        )}
        {equipment.model && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Model</p>
            <p className="font-medium">{equipment.model}</p>
          </div>
        )}
      </div>

      {equipment.nextServiceDate && (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>Next Service: {formatDate(equipment.nextServiceDate)}</span>
        </div>
      )}
    </div>
  );
}

function BatchResult({ batch, onNavigate }: { batch: BatchScanResult; onNavigate: () => void }) {
  const isExpired = batch.expiryDate && new Date(batch.expiryDate) < new Date();
  const isExpiringSoon =
    batch.expiryDate &&
    new Date(batch.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold">Batch: {batch.batchNumber}</h3>
          <p className="text-sm text-muted-foreground">
            Item: {batch.itemName} ({batch.itemSku})
          </p>
        </div>
        <Button onClick={onNavigate}>
          View Item
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Badge variant="outline">{batch.status}</Badge>
        {isExpired && <Badge variant="destructive">Expired</Badge>}
        {!isExpired && isExpiringSoon && (
          <Badge className="bg-status-warning-subtle text-status-warning-foreground">
            Expiring Soon
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Quantity</p>
          <p className="font-medium">{batch.quantity}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Location</p>
          <p className="font-medium">{batch.locationName}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Expiry Date</p>
          <p className="font-medium">{batch.expiryDate ? formatDate(batch.expiryDate) : 'N/A'}</p>
        </div>
      </div>
    </div>
  );
}
